import { markRaw, ref } from 'vue'
import { defineStore } from 'pinia'
import type { Component } from 'vue'

export type ModalAction<TPayload = unknown> = {
  label: string
  color?: string
  callback?: (payload: TPayload) => void | Promise<void>
}

export type ModalStateAction = {
  label: string
  color?: string
  callback?: (payload: unknown) => void | Promise<void>
}

export type ModalFocusTarget = HTMLElement | null | (() => HTMLElement | null)

export type ModalCloseOptions = {
  focusAfterClose?: ModalFocusTarget
}

export const useModalStore = defineStore('modal', () => {
  const isOpen = ref(false)
  const view = ref<Component | null>(null)
  const data = ref<unknown>(null)
  const actions = ref<ModalStateAction[]>([])
  let pendingFocusAfterClose: (() => HTMLElement | null) | null = null

  function open<TPayload = unknown>(
    nextView: Component,
    nextData: TPayload,
    nextActions: ModalAction<TPayload>[] = [],
  ) {
    isOpen.value = true
    data.value = nextData
    view.value = markRaw(nextView)
    pendingFocusAfterClose = null

    actions.value = nextActions.map((action) => ({
      label: action.label,
      color: action.color,
      callback: action.callback
        ? ((payload: unknown) => action.callback!(payload as TPayload))
        : undefined,
    }))
  }

  function close(options: ModalCloseOptions = {}) {
    const { focusAfterClose } = options

    if (!focusAfterClose) {
      pendingFocusAfterClose = null
    } else if (typeof focusAfterClose === 'function') {
      pendingFocusAfterClose = focusAfterClose
    } else {
      pendingFocusAfterClose = () => focusAfterClose
    }

    isOpen.value = false
    view.value = null
    data.value = null
    actions.value = []
  }

  function consumeFocusAfterClose() {
    const focusTarget = pendingFocusAfterClose
    pendingFocusAfterClose = null
    return focusTarget
  }

  return {
    isOpen,
    view,
    data,
    actions,
    open,
    close,
    consumeFocusAfterClose,
  }
})

export default useModalStore
